import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatError } from "@/lib/api";
import { Search, CheckCircle2, XCircle, Ban, Trash2, Download, Eye, MessageCircle, Send, X, Columns3, Check, UserPlus, Users, Mail, FileSpreadsheet, KeyRound } from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import MemberDetailSheet from "./MemberDetailSheet";
import { Avatar } from "@/components/PhotoUpload";
import { withEmailDeliveryNote } from "@/components/EmailDeliveryNotice";

const STATUS_STYLES = {
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  rejected: "bg-white/5 text-white/50 border-white/10",
  blocked: "bg-red-500/15 text-red-300 border-red-500/30",
};

// Table columns — "name" and "status" are pinned (always shown) so a row is
// always identifiable at a glance; everything else is optional.
const COLUMN_DEFS = [
  { key: "member_id", label: "Member ID" },
  { key: "name", label: "Name", pinned: true },
  { key: "contacts", label: "Contact" },
  { key: "email", label: "Email" },
  { key: "studio", label: "Studio" },
  { key: "city", label: "City" },
  { key: "area", label: "Area" },
  { key: "district", label: "District" },
  { key: "pincode", label: "PIN code" },
  { key: "profile", label: "Profile" },
  { key: "status", label: "Status", pinned: true },
];
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);
const PINNED_KEYS = new Set(COLUMN_DEFS.filter((c) => c.pinned).map((c) => c.key));

// Export fields map to raw stored member fields (independent of the table's
// display columns above — e.g. "name" on screen bundles avatar + full_name +
// mobile, but the export lets you pick full_name and mobile separately).
const EXPORT_FIELDS = [
  { key: "member_id", label: "Member ID" },
  { key: "full_name", label: "Name" },
  { key: "mobile", label: "Mobile" },
  { key: "whatsapp_number", label: "WhatsApp" },
  { key: "telegram_username", label: "Telegram" },
  { key: "email", label: "Email" },
  { key: "studio_name", label: "Studio" },
  { key: "category", label: "Category" },
  { key: "categories", label: "All categories" },
  { key: "city", label: "City" },
  { key: "area", label: "Area" },
  { key: "district", label: "District" },
  { key: "pincode", label: "PIN code" },
  { key: "state", label: "State" },
  { key: "status", label: "Status" },
];

function useClickOutside(onOutside) {
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onOutside]);
  return ref;
}

function CheckboxPanel({ label, icon: Icon, testidPrefix, items, checkedKeys, onToggle, pinnedKeys, footer }) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={`${testidPrefix}-trigger`} onClick={() => setOpen((o) => !o)}
        className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white">
        <Icon size={14} /> {label}
      </button>
      {open && (
        <div className="absolute left-0 z-20 mt-2 w-56 rounded-2xl border border-white/10 bg-[#171720] p-2 shadow-2xl sm:left-auto sm:right-0">
          {items.map((item) => {
            const pinned = pinnedKeys?.has(item.key);
            const checked = checkedKeys.has(item.key);
            return (
              <button key={item.key} type="button" disabled={pinned}
                data-testid={`${testidPrefix}-item-${item.key}`}
                onClick={() => onToggle(item.key)}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-white/80 hover:bg-white/5 disabled:opacity-50 disabled:cursor-default">
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? "border-fuchsia-400 bg-fuchsia-500/30" : "border-white/25"}`}>
                  {checked && <Check size={11} className="text-fuchsia-200" />}
                </span>
                {item.label}
                {pinned && <span className="ml-auto text-[10px] text-white/30">always on</span>}
              </button>
            );
          })}
          {footer && <div className="mt-1 border-t border-white/10 pt-2">{footer}</div>}
        </div>
      )}
    </div>
  );
}

export default function AdminMembers() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [completion, setCompletion] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailFor, setDetailFor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [credentialBusy, setCredentialBusy] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(new Set(ALL_COLUMN_KEYS));
  const [exportFields, setExportFields] = useState(new Set(EXPORT_FIELDS.map((f) => f.key)));

  useEffect(() => {
    api.get("/admin/members/preferences/columns").then((r) => {
      const saved = r.data?.columns;
      if (saved && saved.length) setVisibleColumns(new Set([...saved, ...PINNED_KEYS]));
    }).catch(() => {});
  }, []);

  const toggleColumn = (key) => {
    if (PINNED_KEYS.has(key)) return;
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      api.put("/admin/members/preferences/columns", { columns: Array.from(next) }).catch(() => {});
      return next;
    });
  };
  const toggleExportField = (key) => setExportFields((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/members", { params: { q, status, source, completion, page, limit: 25 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q, status, source, completion, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [q, status, source, completion, page]);

  const toggleOne = (mid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(mid)) next.delete(mid); else next.add(mid);
    return next;
  });
  const allOnPageSelected = data.items.length > 0 && data.items.every((m) => selected.has(m.member_id));
  const toggleAllOnPage = () => setSelected((prev) => {
    if (allOnPageSelected) {
      const next = new Set(prev);
      data.items.forEach((m) => next.delete(m.member_id));
      return next;
    }
    const next = new Set(prev);
    data.items.forEach((m) => next.add(m.member_id));
    return next;
  });

  const bulkVerify = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Verify ${ids.length} selected member${ids.length > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/members/bulk-approve", { member_ids: ids });
      toast.success(`Approved ${res.approved} member${res.approved === 1 ? "" : "s"}${res.failed?.length ? ` (${res.failed.length} failed)` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBulkBusy(false); }
  };

  const bulkReject = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Reject ${ids.length} selected member${ids.length > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/members/bulk-reject", { member_ids: ids });
      toast.success(`Rejected ${res.rejected} member${res.rejected === 1 ? "" : "s"}${res.failed?.length ? ` (${res.failed.length} failed)` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBulkBusy(false); }
  };

  const bulkBlock = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Block ${ids.length} selected member${ids.length > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/members/bulk-block", { member_ids: ids });
      toast.success(`Blocked ${res.blocked} member${res.blocked === 1 ? "" : "s"}${res.failed?.length ? ` (${res.failed.length} failed)` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBulkBusy(false); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected member${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/members/bulk-delete", { member_ids: ids });
      toast.success(`Deleted ${res.deleted} member${res.deleted === 1 ? "" : "s"}${res.failed?.length ? ` (${res.failed.length} failed)` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBulkBusy(false); }
  };

  const act = async (mid, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Delete this member permanently?")) return;
        await api.delete(`/admin/members/${encodeURIComponent(mid)}`);
      } else {
        const { data } = await api.post(`/admin/members/${encodeURIComponent(mid)}/${action}`);
        if (action === "approve" && data?.username) {
          window.alert(
            `Approved.\n\nUsername: ${data.username}\n\n` +
            `Open the member and click "Send login details". They will receive an ` +
            `email with the username and a link to set their own password via Forgot Password.`,
          );
        }
      }
      toast.success(`Member ${action}d`); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const sendCredentials = async (member) => {
    if (!member.email) {
      toast.error("This member has no email address");
      return;
    }
    if (!member.username) {
      toast.error("Approve this member before sending login details");
      return;
    }
    if (!window.confirm(`Send login details to ${member.email}?`)) return;

    setCredentialBusy(member.member_id);
    try {
      const { data: result } = await api.post(`/admin/members/${encodeURIComponent(member.member_id)}/send-login-details`);
      if (result.email_sent) {
        toast.success(withEmailDeliveryNote(`Login details sent to ${member.email}.`));
      } else {
        toast.error("The email could not be delivered. Check the mail settings and try again.");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCredentialBusy(null);
    }
  };

  const exportCsv = async () => {
    if (!exportFields.size) { toast.error("Pick at least one field to export"); return; }
    const fields = EXPORT_FIELDS.filter((f) => exportFields.has(f.key)).map((f) => f.key).join(",");
    try {
      const r = await fetch(`${API}/admin/members/actions/export?fields=${encodeURIComponent(fields)}`, { credentials: "include" });
      if (!r.ok) {
        // A failed request (auth, server error) still resolves here rather
        // than rejecting — without this check the error body silently gets
        // saved as if it were the CSV, downloading a file with no real data.
        let detail = `Export failed (${r.status})`;
        try { detail = (await r.json())?.detail || detail; } catch {}
        toast.error(detail);
        return;
      }
      const b = await r.blob();
      if (!b.size) { toast.error("The export is empty because no members matched."); return; }
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pronet_members.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Safari/mobile browsers may not begin reading the blob until after the
      // click handler returns, so revoking immediately cancels the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Member CSV downloaded");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const exportIncompleteProfiles = async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status !== "all") params.set("status", status);
    if (source !== "all") params.set("source", source);
    try {
      const r = await fetch(`${API}/admin/member-exports/incomplete.xlsx?${params.toString()}`, { credentials: "include" });
      if (!r.ok) {
        let detail = `Export failed (${r.status})`;
        try { detail = (await r.json())?.detail || detail; } catch {}
        return toast.error(detail);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "pronet_incomplete_profiles.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Incomplete profile Excel downloaded");
    } catch (error) { toast.error(formatError(error)); }
  };

  const openEmailFor = (memberIds) => {
    const ids = memberIds.filter(Boolean);
    if (!ids.length) return toast.error("Select at least one member with an email address");
    navigate(`/admin/email?mode=broadcast&members=${encodeURIComponent(ids.join(","))}`);
  };

  const emailSelected = () => {
    const ids = data.items
      .filter((member) => selected.has(member.member_id) && member.email)
      .map((member) => member.member_id);
    if (!ids.length) return toast.error("The selected members do not have email addresses");
    openEmailFor(ids);
  };

  return (
    <div className="space-y-6">
      {/* relative z-30: the fade-up animation's fill-mode:both leaves this
          row's computed transform as matrix(1,0,0,1,0,0) rather than the
          literal keyword "none" even after it finishes, which per spec
          still creates a stacking context — trapping the Views/Export CSV
          dropdowns' z-index below the search bar sibling that follows in
          the DOM. Promoting the whole row's own stacking level fixes it. */}
      <div className="admin-page-heading relative z-30 fade-up">
        <div>
          <div className="admin-page-kicker">Community directory</div>
          <h1 className="mt-2 font-display text-5xl text-white">Members</h1>
          <p className="admin-page-description">Search, review and manage every photographer profile from one operational table.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CheckboxPanel
            label="Views" icon={Columns3} testidPrefix="admin-columns"
            items={COLUMN_DEFS} checkedKeys={visibleColumns} onToggle={toggleColumn} pinnedKeys={PINNED_KEYS}
          />
          <CheckboxPanel
            label="Export CSV" icon={Download} testidPrefix="admin-export"
            items={EXPORT_FIELDS} checkedKeys={exportFields} onToggle={toggleExportField}
            footer={
              <button type="button" onClick={exportCsv} data-testid="admin-export-download"
                className="btn-glow flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white">
                <Download size={12} /> Download
              </button>
            }
          />
        </div>
      </div>

      <div className="admin-surface p-4">
        <div className="flex gap-2 overflow-x-auto pb-4" role="tablist" aria-label="Member source">
          <button type="button" role="tab" aria-selected={source === "all" && completion === "all"} data-testid="admin-source-all"
            onClick={() => { setSource("all"); setCompletion("all"); setPage(1); }}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-semibold transition ${source === "all" && completion === "all" ? "border-violet-500/50 bg-violet-500/15 text-white" : "border-white/10 bg-white/[.025] text-white/55 hover:text-white"}`}>
            <Users size={14} /> All members
            <span className="rounded-full bg-white/10 px-2 py-0.5 tabular-nums">{data.source_counts?.all ?? "-"}</span>
          </button>
          <button type="button" role="tab" aria-selected={source === "add_friend" && completion === "all"} data-testid="admin-source-add-friend"
            onClick={() => { setSource("add_friend"); setCompletion("all"); setPage(1); }}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-semibold transition ${source === "add_friend" && completion === "all" ? "border-fuchsia-500/50 bg-fuchsia-500/15 text-white" : "border-white/10 bg-white/[.025] text-white/55 hover:text-white"}`}>
            <UserPlus size={14} /> Friend referrals
            <span className="rounded-full bg-white/10 px-2 py-0.5 tabular-nums">{data.source_counts?.add_friend ?? "-"}</span>
          </button>
          <button type="button" role="tab" aria-selected={completion === "incomplete"} data-testid="admin-source-incomplete"
            onClick={() => { setSource("all"); setCompletion("incomplete"); setPage(1); }}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-semibold transition ${completion === "incomplete" ? "border-amber-400/40 bg-amber-500/10 text-amber-100" : "border-white/10 bg-white/[.025] text-white/55 hover:text-white"}`}>
            <UserPlus size={14} /> Incomplete profiles
            <span className="rounded-full bg-white/10 px-2 py-0.5 tabular-nums">{data.source_counts?.incomplete ?? "-"}</span>
          </button>
        </div>
        {completion === "incomplete" && (
          <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-400/20 bg-amber-500/[.06] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="admin-incomplete-tools">
            <div>
              <div className="text-sm font-semibold text-amber-100">Profile completion follow-up</div>
              <div className="mt-1 text-xs leading-5 text-white/50">Review missing details, email members directly, or export this filtered list for external follow-up.</div>
            </div>
            <button type="button" onClick={exportIncompleteProfiles} data-testid="admin-export-incomplete"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-amber-300/25 bg-amber-400/10 px-4 py-2.5 text-xs font-semibold text-amber-100 hover:bg-amber-400/15">
              <FileSpreadsheet size={14} /> Export incomplete Excel
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 border-t border-white/8 pt-4">
          <div className="relative min-w-[240px] flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input data-testid="admin-members-search"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              placeholder={completion === "incomplete" ? "Search incomplete profiles…" : source === "add_friend" ? "Search friend referrals…" : "Search name, mobile, member id, studio…"}
              value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger data-testid="admin-members-status" className="w-full sm:w-44 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="admin-surface flex flex-wrap items-center gap-3 p-3" data-testid="admin-bulk-bar">
          <div className="text-sm text-white/70 px-1">{selected.size} selected</div>
          <button onClick={bulkVerify} disabled={bulkBusy} data-testid="admin-bulk-verify"
            className="btn-glow inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
            <CheckCircle2 size={13} /> {bulkBusy ? "Working…" : "Verify selected"}
          </button>
          <button onClick={bulkReject} disabled={bulkBusy} data-testid="admin-bulk-reject"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-white/70 hover:bg-white/5 disabled:opacity-60">
            <XCircle size={13} /> {bulkBusy ? "Working…" : "Reject selected"}
          </button>
          <button onClick={bulkBlock} disabled={bulkBusy} data-testid="admin-bulk-block"
            className="inline-flex items-center gap-2 rounded-full border border-red-500/25 bg-red-500/10 px-4 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/15 disabled:opacity-60">
            <Ban size={13} /> {bulkBusy ? "Working…" : "Block selected"}
          </button>
          <button onClick={bulkDelete} disabled={bulkBusy} data-testid="admin-bulk-delete"
            className="inline-flex items-center gap-2 rounded-full border border-red-500/25 bg-red-500/10 px-4 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/15 disabled:opacity-60">
            <Trash2 size={13} /> {bulkBusy ? "Working…" : "Delete selected"}
          </button>
          {completion === "incomplete" && (
            <button onClick={emailSelected} disabled={bulkBusy} data-testid="admin-bulk-email-incomplete"
              className="inline-flex items-center gap-2 rounded-full border border-sky-400/25 bg-sky-500/10 px-4 py-2 text-xs font-semibold text-sky-200 hover:bg-sky-500/15 disabled:opacity-60">
              <Mail size={13} /> Email selected
            </button>
          )}
          <button onClick={() => setSelected(new Set())} data-testid="admin-bulk-clear"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs text-white/50 hover:text-white">
            <X size={13} /> Clear
          </button>
        </div>
      )}

      <div className="admin-surface -mx-2 overflow-x-auto sm:mx-0">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-white/70 bg-white/5">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" data-testid="admin-select-all"
                  checked={allOnPageSelected} onChange={toggleAllOnPage}
                  className="h-4 w-4 rounded border-white/20 bg-white/5 accent-fuchsia-500" />
              </th>
              {visibleColumns.has("member_id") && <th className="text-left px-4 py-3 font-semibold">Member ID</th>}
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              {visibleColumns.has("contacts") && <th className="text-left px-4 py-3 font-semibold">Contacts</th>}
              {(visibleColumns.has("email") || completion === "incomplete") && <th className="text-left px-4 py-3 font-semibold">Email</th>}
              {visibleColumns.has("studio") && <th className="text-left px-4 py-3 font-semibold">Studio</th>}
              {visibleColumns.has("city") && <th className="text-left px-4 py-3 font-semibold">City</th>}
              {visibleColumns.has("area") && <th className="text-left px-4 py-3 font-semibold">Area</th>}
              {visibleColumns.has("district") && <th className="text-left px-4 py-3 font-semibold">District</th>}
              {visibleColumns.has("pincode") && <th className="text-left px-4 py-3 font-semibold">PIN code</th>}
              {(visibleColumns.has("profile") || completion === "incomplete") && <th className="text-left px-4 py-3 font-semibold">Profile</th>}
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={4 + visibleColumns.size} className="px-4 py-10 text-center text-white/50">Loading…</td></tr>)}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={4 + visibleColumns.size} className="px-4 py-10 text-center text-white/50" data-testid="admin-members-empty">{completion === "incomplete" ? "No incomplete profiles match these filters." : source === "add_friend" ? "No friend referrals found." : "No members found."}</td></tr>
            )}
            {data.items.map((m) => (
              <tr key={m.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`row-${m.member_id}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" data-testid={`select-${m.member_id}`}
                    checked={selected.has(m.member_id)} onChange={() => toggleOne(m.member_id)}
                    className="h-4 w-4 rounded border-white/20 bg-white/5 accent-fuchsia-500" />
                </td>
                {visibleColumns.has("member_id") && <td className="px-4 py-3 font-mono text-xs text-fuchsia-300/80">{m.member_id}</td>}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar src={m.photo_url} name={m.full_name} avatarKey={m.avatar_key} size={36} />
                    <div>
                      <div className="text-white font-medium">{m.full_name}</div>
                      {m.username && <div className="text-[11px] text-fuchsia-300/80 font-mono" data-testid={`row-username-${m.member_id}`}>{m.username}</div>}
                      <div className="text-xs text-white/40 tabular-nums">{m.mobile}</div>
                    </div>
                  </div>
                </td>
                {visibleColumns.has("contacts") && (
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-white/70">
                      {m.whatsapp_number && (
                        <span title={`WhatsApp: ${m.whatsapp_number}`} className="text-emerald-400"><MessageCircle size={12} /></span>
                      )}
                      {(m.telegram_username || m.telegram_number) && (
                        <span title={`Telegram: ${m.telegram_username || m.telegram_number}`} className="text-sky-400"><Send size={12} /></span>
                      )}
                      {m.preferred_contact_method && m.preferred_contact_method !== "any" && (
                        <span className="text-[10px] uppercase tracking-widest text-fuchsia-300/70">pref: {m.preferred_contact_method}</span>
                      )}
                    </div>
                  </td>
                )}
                {(visibleColumns.has("email") || completion === "incomplete") && (
                  <td className="px-4 py-3 text-xs text-white/65">
                    <span className="block max-w-[220px] truncate" title={m.email || "No email on file"}>{m.email || "No email"}</span>
                  </td>
                )}
                {visibleColumns.has("studio") && <td className="px-4 py-3 text-white/70">{m.studio_name || "-"}</td>}
                {visibleColumns.has("city") && <td className="px-4 py-3 text-white/70">{[m.city, m.state].filter(Boolean).join(", ") || "-"}</td>}
                {visibleColumns.has("area") && <td className="px-4 py-3 text-white/70">{m.area || "-"}</td>}
                {visibleColumns.has("district") && <td className="px-4 py-3 text-white/70">{m.district || "-"}</td>}
                {visibleColumns.has("pincode") && <td className="px-4 py-3 text-white/70 tabular-nums">{m.pincode || "-"}</td>}
                {(visibleColumns.has("profile") || completion === "incomplete") && (
                  <td className="px-4 py-3">
                    <div className="min-w-[130px]">
                      <div className="flex items-center justify-between gap-2 text-[11px]"><span className="text-white/55">{m.profile_completion_percent ?? 0}%</span><span className={m.profile_is_complete ? "text-emerald-300" : "text-amber-300"}>{m.profile_is_complete ? "Complete" : "Needs details"}</span></div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-400" style={{ width: `${m.profile_completion_percent ?? 0}%` }} /></div>
                      {!m.profile_is_complete && <div className="mt-1 max-w-[180px] truncate text-[10px] text-white/35" title={(m.profile_missing_fields || []).join(", ")}>{(m.profile_missing_fields || []).slice(0, 2).join(", ")}</div>}
                    </div>
                  </td>
                )}
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${STATUS_STYLES[m.status] || STATUS_STYLES.pending}`}>
                    {m.status || "pending"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <IconBtn testid={`view-${m.member_id}`} onClick={() => setDetailFor(m.member_id)} title="View & edit" tone="text-white/70 hover:text-fuchsia-300" Icon={Eye} />
                    <IconBtn
                      testid={`send-credentials-${m.member_id}`}
                      onClick={() => sendCredentials(m)}
                      title={!m.email ? "No email address" : !m.username ? "Approve member before sending login details" : "Send login details"}
                      tone={m.email && m.username ? "text-violet-300 hover:bg-violet-500/15" : "text-white/20 cursor-not-allowed"}
                      Icon={KeyRound}
                      disabled={credentialBusy === m.member_id || !m.email || !m.username}
                    />
                    {completion === "incomplete" && <IconBtn testid={`email-${m.member_id}`} onClick={() => m.email ? openEmailFor([m.member_id]) : toast.error("This member has no email address")} title={m.email ? "Send custom email" : "No email address"} tone={m.email ? "text-sky-300 hover:bg-sky-500/15" : "text-white/20 cursor-not-allowed"} Icon={Mail} />}
                    <IconBtn testid={`approve-${m.member_id}`} onClick={() => act(m.member_id, "approve")} title="Approve" tone="text-emerald-400 hover:bg-emerald-500/15" Icon={CheckCircle2} />
                    <IconBtn testid={`reject-${m.member_id}`} onClick={() => act(m.member_id, "reject")} title="Reject" tone="text-white/70 hover:bg-white/10" Icon={XCircle} />
                    <IconBtn testid={`block-${m.member_id}`} onClick={() => act(m.member_id, "block")} title="Block" tone="text-red-400 hover:bg-red-500/15" Icon={Ban} />
                    <IconBtn testid={`delete-${m.member_id}`} onClick={() => act(m.member_id, "delete")} title="Delete" tone="text-red-500 hover:bg-red-500/20" Icon={Trash2} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.total > 25 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-white/50">Page {page} · {data.total} total</div>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="btn-ghost rounded-full px-4 py-1.5 disabled:opacity-40 text-white" data-testid="admin-page-prev">Prev</button>
            <button disabled={page * 25 >= data.total} onClick={() => setPage((p) => p + 1)} className="btn-ghost rounded-full px-4 py-1.5 disabled:opacity-40 text-white" data-testid="admin-page-next">Next</button>
          </div>
        </div>
      )}

      <MemberDetailSheet
        memberId={detailFor}
        open={!!detailFor}
        onOpenChange={(o) => { if (!o) setDetailFor(null); }}
        onSaved={load}
      />
    </div>
  );
}

function IconBtn({ Icon, testid, onClick, title, tone, disabled = false }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} title={title} disabled={disabled}
      className={`p-2 rounded-lg transition-all disabled:opacity-50 ${tone}`}><Icon size={15} /></button>
  );
}
